/* Les comptes ouverts à une usine.
 *
 * Une fiche d'abord, l'invitation ensuite — comme pour un agent : la fiche
 * existe avant le compte, et c'est l'adresse vérifiée du lien qui les rattache
 * à la première connexion.
 *
 * Ce qu'on ouvre ici n'est pas un accès de plus à Omnix Desk : c'est un autre
 * outil, qui ne partage avec celui-ci que la porte d'entrée. L'écran le dit,
 * parce qu'ouvrir un accès à un fournisseur n'est pas un geste anodin.
 */

import { useState } from "react";
import { Banner, Empty, Section, Tag } from "@/ui/primitives";
import { Icon } from "@/ui/Icon";
import { Table } from "@/ui/Table";
import { useToast } from "@/ui/Toast";
import { dateFr } from "@/lib/format";
import { inviter } from "@/features/agents/actions";
import { creerAccesUsine } from "@/features/espace-usine/actions";
import type { Profil } from "@/types/metier";

function etatDe(a: Profil): { nom: string; ton: string } {
  if (!a.actif) return { nom: "suspendu", ton: "crit" };
  if (!a.compte_id) return { nom: a.invite_le ? "invité" : "sans compte", ton: "warn" };
  return { nom: "actif", ton: "ok" };
}

export function AccesUsine({
  usineId, nomUsine, comptes, onChange,
}: {
  usineId: string;
  nomUsine: string;
  comptes: Profil[];
  onChange: () => void;
}) {
  const toast = useToast();
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState<string | null>(null);

  const ouvrir = async () => {
    if (!nom.trim() || !email.includes("@")) {
      setErreur("Un nom et une adresse valide.");
      return;
    }
    setOccupe(true);
    setErreur(null);
    try {
      await creerAccesUsine(usineId, nom, email);
      setNom("");
      setEmail("");
      onChange();
      toast("Accès ouvert. Invitez le contact pour qu'il pose son mot de passe.");
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "L'accès n'a pas été ouvert.");
    } finally {
      setOccupe(false);
    }
  };

  const envoyer = async (a: Profil) => {
    setEnvoi(a.id);
    try {
      await inviter(a);
      toast(`Invitation envoyée à ${a.email}.`);
      onChange();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : "L'invitation a échoué.");
    } finally {
      setEnvoi(null);
    }
  };

  return (
    <>
      <Section titre="Accès de l'usine" note={`${comptes.length} comptes`}>
        {comptes.length ? (
          <Table
            lignes={comptes}
            cle={(a) => a.id}
            colonnes={[
              { cle: "nom", titre: "Contact", filtre: "texte", sous: (a) => a.email },
              { cle: "email", titre: "Adresse", type: "ref", mobile: false },
              { cle: "invite_le", titre: "Invité le", type: "date", mobile: false,
                texte: (a) => (a.invite_le ? dateFr(a.invite_le) : "—") },
              { cle: "actif", titre: "Compte", filtre: "select",
                texte: (a) => etatDe(a).nom,
                rendu: (a) => {
                  const e = etatDe(a);
                  return <Tag ton={e.ton} dot>{e.nom}</Tag>;
                } },
              { cle: "inviter", titre: "", court: "", mobile: false,
                rendu: (a) =>
                  a.compte_id ? (
                    <span className="no">—</span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={envoi === a.id}
                      onClick={() => void envoyer(a)}
                    >
                      {envoi === a.id ? "Envoi…" : a.invite_le ? "Relancer" : "Inviter"}
                    </button>
                  ) },
            ]}
          />
        ) : (
          <Empty
            titre="Aucun accès ouvert"
            detail={`Personne chez ${nomUsine} ne peut se connecter pour l'instant.`}
          />
        )}
      </Section>

      <Section titre="Ouvrir un accès" note="une fiche d'abord, l'invitation ensuite">
        <div className="card card-pad">
          {erreur ? <div className="banner">{erreur}</div> : null}
          <div className="fi-form">
            <div className="champ">
              <label htmlFor="au-nom">Nom du contact</label>
              <input
                id="au-nom"
                type="text"
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder="Giulia Ferrero"
              />
            </div>
            <div className="champ">
              <label htmlFor="au-mail">Adresse</label>
              <input
                id="au-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="export@usine.com"
              />
            </div>
          </div>
          <div className="jr-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={occupe}
              onClick={() => void ouvrir()}
            >
              <Icon n="plus" cls="" />
              {occupe ? "Ouverture…" : "Ouvrir l'accès"}
            </button>
          </div>
        </div>
      </Section>

      <Banner>
        Un compte usine n'entre pas dans Omnix Desk : il ouvre un espace à part, où il ne voit que
        sa fiche, ses références, ses documents et les commandes qu'on lui passe — avec le client
        destinataire, puisqu'il expédie. Jamais nos prix de vente, jamais nos marges, jamais les
        autres usines. Les prix qu'il annonce attendent votre décision dans <b>Prix annoncés</b>.
      </Banner>
    </>
  );
}
